<template>
    <div
        class="pin-room-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pin-room-modal-title"
        aria-describedby="pin-room-modal-description"
        @click.self="closePopup"
    >
        <div
            ref="modalDialog"
            class="pin-room-modal__dialog"
        >
            <div class="pin-room-modal__body">
                <div
                    class="pin-room-modal__icon"
                    :class="{
                        'pin-room-modal__icon--unpin': pinnedRoomSTATUS
                    }"
                    aria-hidden="true"
                >
                    <i
                        class="fa"
                        :class="
                            pinnedRoomSTATUS
                                ? 'fa-chain-broken'
                                : 'fa-thumb-tack'
                        "
                    ></i>
                </div>

                <p
                    id="pin-room-modal-description"
                    class="pin-room-modal__message"
                >
                    Are you sure you want to
                    <strong>{{ actionText.toLowerCase() }}</strong>
                    this room?
                </p>

                <p class="pin-room-modal__description">
                    {{ actionDescription }}
                </p>
            </div>

            <footer class="pin-room-modal__footer">
                <button
                    type="button"
                    class="
                        pin-room-modal__button
                        pin-room-modal__button--cancel
                    "
                    :disabled="isSubmitting"
                    @click="closePopup"
                >
                    Cancel
                </button>

                <button
                    ref="confirmButton"
                    type="button"
                    class="
                        pin-room-modal__button
                        pin-room-modal__button--confirm
                    "
                    :disabled="isSubmitting || !hasValidRoomId"
                    @click="confirmPinRoom"
                >
                    <i
                        v-if="isSubmitting"
                        class="fa fa-spinner fa-spin"
                        aria-hidden="true"
                    ></i>

                    <span>
                        {{
                            isSubmitting
                                ? submittingText
                                : confirmButtonText
                        }}
                    </span>
                </button>
            </footer>
        </div>
    </div>
</template>

<script>
import { bus } from '@/main';
import toast from '@/services/toast';

export default {
    name: 'ChatRoomPinnedPopup',

    props: {
        pinnedRoomID: {
            type: [String, Number],
            required: true
        },

        pinnedRoomSTATUS: {
            type: Boolean,
            default: false
        },

        roomName: {
            type: String,
            default: ''
        }
    },

    data() {
        return {
            isSubmitting: false,
            previouslyFocusedElement: null
        };
    },

    computed: {
        actionText() {
            return this.pinnedRoomSTATUS
                ? 'Unpin'
                : 'Pin';
        },

        actionTitle() {
            return this.pinnedRoomSTATUS
                ? 'Unpin'
                : 'Pin';
        },

        nextPinnedStatus() {
            return !this.pinnedRoomSTATUS;
        },

        confirmButtonText() {
            return this.pinnedRoomSTATUS
                ? 'Yes, Unpin'
                : 'Yes, Pin';
        },

        submittingText() {
            return this.pinnedRoomSTATUS
                ? 'Unpinning...'
                : 'Pinning...';
        },

        actionDescription() {
            const normalizedRoomName = String(
                this.roomName || ''
            ).trim();

            const roomReference = normalizedRoomName
                ? '"' + normalizedRoomName + '"'
                : 'this room';

            if (this.pinnedRoomSTATUS) {
                return (
                    roomReference +
                    ' will be moved back to your normal chat list.'
                );
            }

            return (
                roomReference +
                ' will appear at the top of your pinned chat list.'
            );
        },

        hasValidRoomId() {
            return (
                this.pinnedRoomID !== null &&
                this.pinnedRoomID !== undefined &&
                String(this.pinnedRoomID).trim() !== ''
            );
        }
    },

    mounted() {
        this.previouslyFocusedElement =
            document.activeElement;

        document.body.classList.add(
            'body_right_scroll_remove'
        );

        document.addEventListener(
            'keydown',
            this.handleDocumentKeydown
        );

        this.$nextTick(() => {
            if (this.$refs.confirmButton) {
                this.$refs.confirmButton.focus();
            }
        });
    },

    beforeDestroy() {
        document.removeEventListener(
            'keydown',
            this.handleDocumentKeydown
        );

        document.body.classList.remove(
            'body_right_scroll_remove'
        );
    },

    methods: {
        async confirmPinRoom() {
            if (
                this.isSubmitting ||
                !this.hasValidRoomId
            ) {
                if (!this.hasValidRoomId) {
                    toast.error(
                        'Chat room ID is required.'
                    );
                }

                return;
            }

            this.isSubmitting = true;

            const payload = {
                room_id: this.pinnedRoomID,
                pin: this.nextPinnedStatus
            };

            try {
                const response =
                    await this.$store.dispatch(
                        'pinChatRoom',
                        payload
                    );

                await this.refreshRoomLists();

                toast.success(
                    this.getSuccessMessage(
                        response,
                        this.pinnedRoomSTATUS
                            ? 'Room unpinned successfully.'
                            : 'Room pinned successfully.'
                    )
                );

                this.closePopup(true);
            } catch (error) {
                toast.error(
                    this.getErrorMessage(
                        error,
                        this.pinnedRoomSTATUS
                            ? 'Unable to unpin the room.'
                            : 'Unable to pin the room.'
                    )
                );
            } finally {
                this.isSubmitting = false;
            }
        },

        async refreshRoomLists() {
            try {
                await Promise.all([
                    this.$store.dispatch(
                        'fetchPinnedChatRooms'
                    ),
                    this.$store.dispatch(
                        'fetchNormalChatRooms'
                    )
                ]);
            } catch (error) {
                /*
                 * Pinning may already have succeeded.
                 * A list-refresh error should not make the
                 * pin/unpin operation appear unsuccessful.
                 */
                console.error(
                    'Unable to refresh room lists:',
                    error
                );
            }
        },

        closePopup(forceClose) {
            if (
                this.isSubmitting &&
                forceClose !== true
            ) {
                return;
            }

            document.body.classList.remove(
                'body_right_scroll_remove'
            );

            bus.$emit(
                'pinnedRoomPopupStateBus',
                false
            );

            this.restoreFocus();
        },

        restoreFocus() {
            const previousElement =
                this.previouslyFocusedElement;

            this.$nextTick(() => {
                if (
                    previousElement &&
                    typeof previousElement.focus ===
                        'function'
                ) {
                    previousElement.focus();
                }

                this.previouslyFocusedElement = null;
            });
        },

        handleDocumentKeydown(event) {
            if (
                event.key === 'Escape' &&
                !this.isSubmitting
            ) {
                this.closePopup();
            }
        },

        getSuccessMessage(response, fallbackMessage) {
            if (!response || !response.data) {
                return fallbackMessage;
            }

            return (
                response.data.user_msg ||
                response.data.msg ||
                response.data.message ||
                fallbackMessage
            );
        },

        getErrorMessage(error, fallbackMessage) {
            const responseData =
                error &&
                error.response &&
                error.response.data
                    ? error.response.data
                    : error && error.data
                        ? error.data
                        : null;

            if (!responseData) {
                return error && error.message
                    ? error.message
                    : fallbackMessage;
            }

            const nestedData =
                responseData.data &&
                typeof responseData.data === 'object'
                    ? responseData.data
                    : null;

            if (
                nestedData &&
                Array.isArray(
                    nestedData.non_field_errors
                ) &&
                nestedData.non_field_errors.length
            ) {
                return nestedData.non_field_errors[0];
            }

            if (
                Array.isArray(
                    responseData.non_field_errors
                ) &&
                responseData.non_field_errors.length
            ) {
                return responseData.non_field_errors[0];
            }

            return (
                responseData.user_msg ||
                responseData.msg ||
                responseData.message ||
                fallbackMessage
            );
        }
    }
};
</script>

<style lang="scss" scoped>
$primary-text: #ffffff;
$secondary-text: #aaaaaa;
$accent-color: #87cefa;
$highlight-color: #ffe738;
$success-color: #21e400;
$danger-color: #ff3a30;
$dialog-background: #000000;
$overlay-background: rgba(0, 0, 0, 0.92);

.pin-room-modal {
    position: fixed;
    inset: 0;
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    overflow-y: auto;
    background-color: $overlay-background;

    &__dialog {
        width: 100%;
        max-width: 460px;
        overflow: hidden;
        border-radius: 6px;
        color: $primary-text;
        background-color: $dialog-background;
        box-shadow: 0 0 13px rgba(
            255,
            213,
            0,
            0.6
        );
        font-family:
            'MyriadProRegular',
            'Lato',
            sans-serif;
    }

    &__body {
        padding: 26px 20px 22px;
        text-align: center;
    }

    &__icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 64px;
        height: 64px;
        margin-bottom: 16px;
        border-radius: 50%;
        color: $success-color;
        background-color: rgba(
            33,
            228,
            0,
            0.12
        );
        font-size: 28px;

        &--unpin {
            color: $danger-color;
            background-color: rgba(
                255,
                58,
                48,
                0.12
            );
        }
    }

    &__message {
        margin: 0;
        color: $primary-text;
        font-size: 18px;
        line-height: 1.5;

        strong {
            color: $accent-color;
        }
    }

    &__description {
        margin: 10px 0 0;
        color: $secondary-text;
        font-size: 14px;
        line-height: 1.5;
    }

    &__footer {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 12px;
        padding: 0 20px 20px;
    }

    &__button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 110px;
        min-height: 40px;
        padding: 8px 14px;
        border: 0;
        border-radius: 4px;
        color: $primary-text;
        background-color: transparent;
        font-family: inherit;
        font-size: 16px;
        cursor: pointer;
        transition:
            color 0.2s ease,
            background-color 0.2s ease,
            opacity 0.2s ease;

        .fa {
            margin-right: 7px;
        }

        &:focus {
            outline: 2px solid $highlight-color;
            outline-offset: 2px;
        }

        &:disabled {
            cursor: not-allowed;
            opacity: 0.55;
        }

        &--cancel {
            background-color: rgba(
                255,
                58,
                48,
                0.45
            );

            &:hover:not(:disabled){
                color: #22292f;
                background-color: $highlight-color;
            }
        }

        &--confirm {
            background-color: rgba(
                33,
                228,
                0,
                0.5
            );

            &:hover:not(:disabled) {
                color: #22292f;
                background-color: $highlight-color;
            }
        }
    }
}

@media only screen and (max-width: 575px) {
    .pin-room-modal {
        // align-items: flex-end;
        padding: 10px;

        &__dialog {
            max-width: 100%;
        }

        &__header {
            padding: 16px;
        }

        &__body {
            padding: 22px 16px;
        }

        &__footer {
            flex-direction: column-reverse;
            padding: 0 16px 16px;
        }

        &__button {
            width: 100%;
        }
    }
}
</style>